/**
 * SlackThreadPanel — slide-in right panel for Slack discussions.
 * Works for any entity: milestones, drawing sheets, BIM systems.
 *
 * Props:
 *   entityId      — Airtable record ID of the entity being discussed
 *   entityType    — 'milestone' | 'sheet' | 'bim'
 *   entityName    — display name shown in the panel header
 *   projectId     — Airtable Project record ID (used to resolve the Slack channel)
 *   projectName   — display name for the project
 *   threadTs      — initial Slack thread timestamp ('' if no thread yet)
 *   onClose       — called when the panel is dismissed
 *   onThreadStarted(ts) — called after a new thread is successfully created;
 *                         caller is responsible for updating its own cache/state
 *   mentionName   — optional: a person's name to @mention at the start of the reply box once
 *                   the workspace members load (the triage's "Talk to the creator" tile)
 *   onMessageSent(text) — called right after a text reply successfully posts (not
 *                         image uploads). AlderCAD uses this to broadcast a live
 *                         speech-bubble over the sender's walk-mode avatar — purely
 *                         optional view chrome, unrelated to Slack persistence.
 *   variant       — 'overlay' (default): the outline app's slide-in-from-the-right
 *                    panel, portaled to document.body with a full-screen click-to-close
 *                    backdrop. 'dock': renders INLINE (no portal, no backdrop) so the
 *                    caller can seat it in its own layout — fills whatever flex/sized
 *                    container it's placed in. Used by AlderCAD (ModelerPage.jsx), which
 *                    docks it in the lower portion of the left rail instead of overlaying
 *                    the canvas, so a conversation stays visible while working.
 *
 * Panel states:
 *   no thread      → chat icon + "Start Discussion" button
 *   loading thread → "Loading…" placeholder
 *   thread exists  → MessageRow list + reply input at bottom
 *   error          → inline error message with Dismiss link
 *
 * Opens with a CSS transition (requestAnimationFrame → add --open class).
 * Closes with reverse transition (remove --open → wait 220ms → call onClose).
 *
 * Read state: localStorage key 'slack:read:{entityId}' stores the ts of the
 * last seen message. The unread dot on the row is driven by comparing this
 * value to the entity's Slack Thread Timestamp field.
 *
 * @mentions: type @ to trigger autocomplete. Members loaded once on mount via
 * getMembers(). Selected member inserts @slack_username; link_names:true on
 * the API resolves it to a proper Slack mention.
 *
 * Keyboard: Enter sends, Shift+Enter inserts a newline (matches Slack itself).
 *
 * Message rendering: full Slack mrkdwn — *bold*, _italic_, ~strike~, `code`,
 * ```blocks```, <URL|text> hyperlinks, <@mentions>, <!here/channel/everyone>,
 * and > blockquotes.
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { startDiscussion, getThread, replyToThread, uploadImage, getMembers, checkSlackConnection, getSlackConnectUrl, deleteMessage, addReaction, removeReaction } from '../services/slack.js'

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function formatTs(ts) {
  if (!ts) return ''
  const d = new Date(parseFloat(ts) * 1000)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function initials(name) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Slack's file URLs (url_private) require the bot token to fetch — a browser
// <img src> can't carry one, so posted images route through api/slack-file.js.
function imageProxyUrl(url) {
  return '/api/slack-file?url=' + encodeURIComponent(url)
}

/** Find an in-progress @mention at the cursor position */
function getMentionAtCursor(text, cursor) {
  const before = text.slice(0, cursor)
  const match = before.match(/@(\w*)$/)
  if (!match) return null
  return { query: match[1], start: cursor - match[0].length }
}

/* ── Slack mrkdwn renderer ───────────────────────────────────────────────── */

/**
 * Tokenize Slack mrkdwn in a single line (no newlines).
 * Returns an array of strings (plain text) and React elements (formatted).
 *
 * Handles (in priority order):
 *   ```code block```   multi-line code fence
 *   `inline code`
 *   *bold*
 *   _italic_
 *   ~strikethrough~
 *   <@USERID>          user mention
 *   <#CID|name>        channel mention
 *   <!here|channel|everyone>  broadcast
 *   <URL|label>        hyperlink with label
 *   <URL>              bare hyperlink
 */
function renderInline(text, members, keyBase) {
  // Fresh regex on each call (g flag is stateful).
  // Bare https?:// URLs are matched last — after <URL> Slack-format links — so they
  // only catch plain URLs that weren't already wrapped in angle brackets by Slack.
  const TOKEN = /(<@[A-Z0-9]+(?:\|[^>]*)?>|<#[A-Z0-9]+\|[^>]+>|<!(?:here|channel|everyone)(?:\|[^>]*)?>|<https?:[^>]+>|```[\s\S]*?```|`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|https?:\/\/[^\s<>"']+)/g

  const parts = []
  let last = 0
  let k = 0
  let match

  while ((match = TOKEN.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index))
    parts.push(renderSlackToken(match[0], members, `${keyBase}-${k++}`))
    last = match.index + match[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts
}

function renderSlackToken(token, members, key) {
  /* ── user mention ── */
  if (token.startsWith('<@')) {
    const inner = token.slice(2, -1)           // "USERID" or "USERID|name"
    const [uid] = inner.split('|')
    const m = members.find(m => m.id === uid)
    const name = m ? (m.displayName || m.realName || m.name) : uid
    return (
      <span key={key} style={{
        background: 'rgba(29,158,117,0.10)', color: '#1D9E75',
        borderRadius: 3, padding: '0 3px', fontWeight: 600,
      }}>
        @{name}
      </span>
    )
  }

  /* ── channel mention ── */
  if (token.startsWith('<#')) {
    const name = token.slice(token.indexOf('|') + 1, -1)
    return (
      <span key={key} style={{ color: '#1D9E75', fontWeight: 500 }}>
        #{name}
      </span>
    )
  }

  /* ── broadcast (!here / !channel / !everyone) ── */
  if (token.startsWith('<!')) {
    const end = token.includes('|') ? token.indexOf('|') : token.length - 1
    const type = token.slice(2, end)
    return (
      <span key={key} style={{
        background: 'rgba(29,158,117,0.10)', color: '#1D9E75',
        borderRadius: 3, padding: '0 3px', fontWeight: 600,
      }}>
        @{type}
      </span>
    )
  }

  /* ── hyperlink ── */
  if (token.startsWith('<http')) {
    const inner = token.slice(1, -1)           // "URL" or "URL|label"
    const pipeIdx = inner.indexOf('|')
    const url   = pipeIdx !== -1 ? inner.slice(0, pipeIdx) : inner
    const label = pipeIdx !== -1 ? inner.slice(pipeIdx + 1) : url
    return (
      <a key={key} href={url} target="_blank" rel="noopener noreferrer"
        style={{ color: '#1D6FA8', textDecoration: 'underline', wordBreak: 'break-word' }}>
        {label}
      </a>
    )
  }

  /* ── code block `` `` `` ── */
  if (token.startsWith('```')) {
    const content = token.slice(3, -3).replace(/^\n/, '').replace(/\n$/, '')
    return (
      <code key={key} style={{
        display: 'block',
        background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)',
        borderRadius: 4, padding: '5px 8px', margin: '3px 0',
        fontFamily: '"DM Mono",monospace', fontSize: 11,
        whiteSpace: 'pre-wrap', overflowX: 'auto',
      }}>
        {content}
      </code>
    )
  }

  /* ── inline code ── */
  if (token.startsWith('`')) {
    return (
      <code key={key} style={{
        background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)',
        borderRadius: 3, padding: '1px 4px',
        fontFamily: '"DM Mono",monospace', fontSize: 11,
      }}>
        {token.slice(1, -1)}
      </code>
    )
  }

  /* ── bold ── */
  if (token.startsWith('*')) {
    return <strong key={key} style={{ fontWeight: 700 }}>{token.slice(1, -1)}</strong>
  }

  /* ── italic ── */
  if (token.startsWith('_')) {
    return <em key={key}>{token.slice(1, -1)}</em>
  }

  /* ── strikethrough ── */
  if (token.startsWith('~')) {
    return <del key={key}>{token.slice(1, -1)}</del>
  }

  /* ── bare URL (https?://...) ── */
  if (token.startsWith('http')) {
    return (
      <a key={key} href={token} target="_blank" rel="noopener noreferrer"
        style={{ color: '#1D6FA8', textDecoration: 'underline', wordBreak: 'break-word' }}>
        {token}
      </a>
    )
  }

  return token   // plain text fallback
}

/**
 * Full Slack mrkdwn → React elements.
 * Handles newlines and blockquote lines ("> ...") before inline formatting.
 */
function SlackText({ text, members }) {
  if (!text) return null

  const lines = text.split('\n')
  const result = []

  lines.forEach((line, li) => {
    if (li > 0) result.push(<br key={`br-${li}`} />)

    if (line.startsWith('> ') || line === '>') {
      const content = line.startsWith('> ') ? line.slice(2) : ''
      result.push(
        <span key={`bq-${li}`} style={{
          display: 'block', borderLeft: '3px solid var(--aj-border-mid)',
          paddingLeft: 8, color: 'var(--aj-text-mid)', margin: '1px 0',
        }}>
          {renderInline(content, members, `bq-${li}`)}
        </span>
      )
    } else {
      result.push(...renderInline(line, members, `ln-${li}`))
    }
  })

  return <>{result}</>
}

/* ── Reactions ───────────────────────────────────────────────────────────── */

// Quick-react bar — the 4 emojis shown on hover for one-click reacting
const QUICK_REACTIONS = [
  { name: '+1',               emoji: '👍' },
  { name: '-1',               emoji: '👎' },
  { name: 'white_check_mark', emoji: '✅' },
  { name: 'eyes',             emoji: '👀' },
]

// Display map — converts Slack API emoji names → unicode for rendering chips.
// Covers the most common Slack emoji shortcodes; falls back to :name: for unknowns.
const EMOJI_MAP = {
  // Thumbs / checks / core reactions
  '+1': '👍', 'thumbsup': '👍', '-1': '👎', 'thumbsdown': '👎',
  'white_check_mark': '✅', 'heavy_check_mark': '✔️', 'ballot_box_with_check': '☑️',
  'x': '❌', 'negative_squared_cross_mark': '❎',
  'eyes': '👀', 'heart': '❤️', 'fire': '🔥', '100': '💯',
  'raised_hands': '🙌', 'clap': '👏', 'pray': '🙏',
  // Punctuation & symbols (common in Slack messages)
  'bangbang': '‼️', 'interrobang': '⁉️', 'exclamation': '❗', 'grey_exclamation': '❕',
  'question': '❓', 'grey_question': '❔',
  'heavy_plus_sign': '➕', 'heavy_minus_sign': '➖', 'heavy_division_sign': '➗',
  'heavy_multiplication_x': '✖️',
  'asterisk': '*️⃣', 'hash': '#️⃣',
  'arrow_right': '➡️', 'arrow_left': '⬅️', 'arrow_up': '⬆️', 'arrow_down': '⬇️',
  'arrow_upper_right': '↗️', 'arrow_lower_right': '↘️', 'arrow_lower_left': '↙️', 'arrow_upper_left': '↖️',
  'arrows_clockwise': '🔃', 'arrows_counterclockwise': '🔄',
  'small_red_triangle': '🔺', 'small_red_triangle_down': '🔻',
  'red_circle': '🔴', 'orange_circle': '🟠', 'yellow_circle': '🟡',
  'green_circle': '🟢', 'blue_circle': '🔵', 'purple_circle': '🟣',
  'brown_circle': '🟤', 'black_circle': '⚫', 'white_circle': '⚪',
  'large_blue_circle': '🔵', 'large_green_circle': '🟢',
  // Smiley faces
  'slightly_smiling_face': '🙂', 'upside_down_face': '🙃',
  'winking_face': '😉', 'wink': '😉',
  'blush': '😊', 'slightly_smiling': '🙂',
  'smiley': '😃', 'smile': '😄', 'grinning': '😀', 'grin': '😁',
  'joy': '😂', 'rofl': '🤣', 'laughing': '😆', 'satisfied': '😆',
  'sweat_smile': '😅', 'innocent': '😇', 'kissing_heart': '😘',
  'kissing': '😗', 'kissing_smiling_eyes': '😙', 'kissing_closed_eyes': '😚',
  'yum': '😋', 'stuck_out_tongue': '😛', 'stuck_out_tongue_winking_eye': '😜',
  'stuck_out_tongue_closed_eyes': '😝', 'money_mouth_face': '🤑',
  'hugs': '🤗', 'hand_over_mouth': '🤭', 'shushing_face': '🤫',
  'thinking': '🤔', 'thinking_face': '🤔', 'zipper_mouth_face': '🤐',
  'raised_eyebrow': '🤨', 'neutral_face': '😐', 'expressionless': '😑',
  'no_mouth': '😶', 'smirk': '😏', 'unamused': '😒',
  'roll_eyes': '🙄', 'face_with_rolling_eyes': '🙄',
  'grimacing': '😬', 'lying_face': '🤥', 'relieved': '😌',
  'pensive': '😔', 'sleepy': '😪', 'drooling_face': '🤤',
  'sleeping': '😴', 'mask': '😷', 'face_with_thermometer': '🤒',
  'face_with_head_bandage': '🤕', 'nauseated_face': '🤢', 'face_vomiting': '🤮',
  'sneezing_face': '🤧', 'hot_face': '🥵', 'cold_face': '🥶',
  'woozy_face': '🥴', 'dizzy_face': '😵', 'exploding_head': '🤯',
  'cowboy_hat_face': '🤠', 'partying_face': '🥳', 'sunglasses': '😎',
  'nerd_face': '🤓', 'monocle_face': '🧐',
  'confused': '😕', 'worried': '😟', 'slightly_frowning_face': '🙁',
  'frowning_face': '☹️', 'open_mouth': '😮', 'hushed': '😯',
  'astonished': '😲', 'flushed': '😳', 'pleading_face': '🥺',
  'anguished': '😧', 'fearful': '😨', 'cold_sweat': '😰',
  'disappointed_relieved': '😥', 'cry': '😢', 'sob': '😭',
  'scream': '😱', 'confounded': '😖', 'persevere': '😣',
  'disappointed': '😞', 'sweat': '😓', 'weary': '😩', 'tired_face': '😫',
  'yawning_face': '🥱', 'triumph': '😤', 'rage': '😡', 'angry': '😠',
  'skull': '💀', 'skull_and_crossbones': '☠️', 'ghost': '👻',
  'alien': '👽', 'space_invader': '👾', 'robot': '🤖',
  'poop': '💩', 'hankey': '💩', 'shit': '💩',
  'smiling_imp': '😈', 'imp': '👿', 'japanese_ogre': '👹', 'japanese_goblin': '👺',
  'clown_face': '🤡', 'lying_face_pinocchio': '🤥',
  'zany_face': '🤪', 'star_struck': '🤩', 'smiling_face_with_3_hearts': '🥰',
  'heart_eyes': '😍', 'kissing_heart_face': '😘',
  // Hands & gestures
  'wave': '👋', 'raised_hand': '✋', 'raised_back_of_hand': '🤚',
  'hand': '✋', 'vulcan_salute': '🖖', 'ok_hand': '👌',
  'pinched_fingers': '🤌', 'pinching_hand': '🤏',
  'point_up': '☝️', 'point_up_2': '👆', 'point_down': '👇',
  'point_left': '👈', 'point_right': '👉',
  'fu': '🖕', 'middle_finger': '🖕',
  'v': '✌️', 'crossed_fingers': '🤞', 'hand_with_index_finger_and_thumb_crossed': '🫰',
  'love_you_gesture': '🤟', 'metal': '🤘', 'call_me_hand': '🤙',
  'muscle': '💪', 'mechanical_arm': '🦾', 'leg': '🦵', 'foot': '🦶',
  'handshake': '🤝', 'open_hands': '👐', 'fist': '✊',
  'facepunch': '👊', 'punch': '👊', 'left_facing_fist': '🤛', 'right_facing_fist': '🤜',
  'writing_hand': '✍️', 'nail_care': '💅',
  // Hearts & love
  'orange_heart': '🧡', 'yellow_heart': '💛', 'green_heart': '💚',
  'blue_heart': '💙', 'purple_heart': '💜', 'brown_heart': '🤎',
  'black_heart': '🖤', 'white_heart': '🤍', 'broken_heart': '💔',
  'heavy_heart_exclamation': '❣️', 'two_hearts': '💕', 'revolving_hearts': '💞',
  'heartbeat': '💓', 'heartpulse': '💗', 'sparkling_heart': '💖',
  'cupid': '💘', 'gift_heart': '💝', 'heart_decoration': '💟',
  'peace_symbol': '☮️', 'cross': '✝️',
  // Celebration & awards
  'tada': '🎉', 'confetti_ball': '🎊', 'balloon': '🎈', 'gift': '🎁',
  'trophy': '🏆', 'medal_sports': '🏅', 'first_place_medal': '🥇',
  'second_place_medal': '🥈', 'third_place_medal': '🥉',
  'star': '⭐', 'star2': '🌟', 'sparkles': '✨', 'dizzy': '💫',
  'boom': '💥', 'collision': '💥',
  // Nature & weather
  'sunny': '☀️', 'partly_sunny': '⛅', 'cloud': '☁️', 'rain_cloud': '🌧️',
  'snow_cloud': '🌨️', 'lightning': '🌩️', 'tornado': '🌪️',
  'rainbow': '🌈', 'umbrella': '☂️', 'snowflake': '❄️',
  'droplet': '💧', 'ocean': '🌊', 'seedling': '🌱',
  'evergreen_tree': '🌲', 'deciduous_tree': '🌳', 'palm_tree': '🌴',
  'cactus': '🌵', 'tulip': '🌷', 'cherry_blossom': '🌸', 'rose': '🌹',
  'sunflower': '🌻', 'bouquet': '💐', 'mushroom': '🍄',
  // Food & drink
  'coffee': '☕', 'tea': '🍵', 'beer': '🍺', 'beers': '🍻',
  'wine_glass': '🍷', 'cocktail': '🍸', 'tropical_drink': '🍹',
  'champagne': '🍾', 'pizza': '🍕', 'hamburger': '🍔', 'fries': '🍟',
  'hotdog': '🌭', 'taco': '🌮', 'burrito': '🌯', 'sandwich': '🥪',
  'salad': '🥗', 'spaghetti': '🍝', 'sushi': '🍣', 'cake': '🎂',
  'cupcake': '🧁', 'cookie': '🍪', 'doughnut': '🍩', 'ice_cream': '🍦',
  'apple': '🍎', 'banana': '🍌', 'grapes': '🍇', 'strawberry': '🍓',
  'avocado': '🥑', 'broccoli': '🥦', 'corn': '🌽',
  // Objects & tools
  'rocket': '🚀', 'airplane': '✈️', 'car': '🚗', 'truck': '🚚',
  'bike': '🚲', 'house': '🏠', 'office': '🏢', 'school': '🏫',
  'hospital': '🏥', 'bank': '🏦', 'hotel': '🏨',
  'phone': '📱', 'telephone': '☎️', 'computer': '💻', 'desktop_computer': '🖥️',
  'keyboard': '⌨️', 'printer': '🖨️', 'mouse_three_button': '🖱️',
  'tv': '📺', 'radio': '📻', 'camera': '📷', 'video_camera': '📹',
  'bulb': '💡', 'flashlight': '🔦', 'battery': '🔋',
  'hammer': '🔨', 'wrench': '🔧', 'screwdriver': '🪛', 'nut_and_bolt': '🔩',
  'gear': '⚙️', 'link': '🔗', 'paperclip': '📎', 'scissors': '✂️',
  'pencil': '✏️', 'pen': '🖊️', 'memo': '📝', 'notebook': '📓',
  'books': '📚', 'book': '📖', 'clipboard': '📋', 'calendar': '📅',
  'date': '📅', 'card_index': '📇', 'chart_with_upwards_trend': '📈',
  'chart_with_downwards_trend': '📉', 'bar_chart': '📊',
  'pushpin': '📌', 'round_pushpin': '📍', 'triangular_flag_on_post': '🚩',
  'white_flag': '🏳️', 'checkered_flag': '🏁', 'black_flag': '🏴',
  'mag': '🔍', 'mag_right': '🔎', 'lock': '🔒', 'unlock': '🔓',
  'key': '🔑', 'old_key': '🗝️', 'moneybag': '💰', 'money_with_wings': '💸',
  'dollar': '💵', 'credit_card': '💳', 'gem': '💎',
  'bell': '🔔', 'no_bell': '🔕', 'mega': '📣', 'loudspeaker': '📢',
  'envelope': '✉️', 'email': '📧', 'incoming_envelope': '📨',
  'mailbox': '📫', 'inbox_tray': '📥', 'outbox_tray': '📤',
  'package': '📦', 'label': '🏷️', 'bookmark': '🔖', 'ticket': '🎫',
  // Misc symbols
  'zap': '⚡', 'hourglass': '⌛', 'hourglass_flowing_sand': '⏳',
  'alarm_clock': '⏰', 'stopwatch': '⏱️', 'timer_clock': '⏲️',
  'clock1': '🕐', 'clock2': '🕑', 'clock3': '🕒', 'clock12': '🕛',
  'sos': '🆘', 'no_entry': '⛔', 'no_entry_sign': '🚫',
  'warning': '⚠️', 'rotating_light': '🚨', 'construction': '🚧',
  'recycle': '♻️', 'put_litter_in_its_place': '🚮',
  'wheelchair': '♿', 'symbols': '🔣', 'information_source': 'ℹ️',
  'new': '🆕', 'free': '🆓', 'up': '🆙', 'cool': '🆒', 'ok': '🆗',
  'ng': '🆖', 'id': '🆔', 'end': '🔚', 'back': '🔙',
  'on': '🔛', 'top': '🔝', 'soon': '🔜',
  'radio_button': '🔘', 'large_blue_diamond': '🔷', 'large_orange_diamond': '🔶',
  'small_blue_diamond': '🔹', 'small_orange_diamond': '🔸',
  // Sport & activity
  'soccer': '⚽', 'basketball': '🏀', 'football': '🏈', 'baseball': '⚾',
  'tennis': '🎾', 'volleyball': '🏐', 'rugby_football': '🏉',
  'ping_pong': '🏓', 'golf': '⛳',
  'running': '🏃', 'walking': '🚶', 'swimmer': '🏊', 'surfer': '🏄',
  'weight_lifter': '🏋️', 'bicyclist': '🚴', 'horse_racing': '🏇',
  'dart': '🎯', 'game_die': '🎲', 'chess_pawn': '♟️',
  // Animals (common)
  'dog': '🐶', 'cat': '🐱', 'mouse': '🐭', 'hamster': '🐹',
  'rabbit': '🐰', 'fox_face': '🦊', 'bear': '🐻', 'panda_face': '🐼',
  'koala': '🐨', 'tiger': '🐯', 'lion': '🦁', 'cow': '🐮',
  'pig': '🐷', 'frog': '🐸', 'monkey': '🐵', 'chicken': '🐔',
  'penguin': '🐧', 'bird': '🐦', 'duck': '🦆', 'eagle': '🦅',
  'owl': '🦉', 'bat': '🦇', 'wolf': '🐺', 'horse': '🐴',
  'unicorn': '🦄', 'bee': '🐝', 'butterfly': '🦋', 'snail': '🐌',
  'shell': '🐚', 'turtle': '🐢', 'snake': '🐍', 'dragon': '🐉',
  'whale': '🐳', 'dolphin': '🐬', 'fish': '🐟', 'shark': '🦈',
  'octopus': '🐙', 'crab': '🦀', 'lobster': '🦞', 'shrimp': '🦐',
}

/* ── MessageRow ──────────────────────────────────────────────────────────── */

function MessageRow({ msg, isFirst, members, currentSlackUserId, onDelete, onReact }) {
  const [hovered, setHovered] = useState(false)
  const [hoveredReaction, setHoveredReaction] = useState(null)

  const isBot = !!msg.bot_id
  const memberMatch = !isBot ? members.find(m => m.id === msg.user) : null
  const authorName =
    msg._authorName ||
    (isBot
      ? (msg.username || msg.bot_profile?.name || 'AlderCortex')
      : (memberMatch?.displayName || memberMatch?.realName || memberMatch?.name ||
         msg.user_profile?.display_name || msg.user_profile?.real_name ||
         msg.username ||
         msg.user || 'Team')
    )

  const isOwn = currentSlackUserId && msg.user === currentSlackUserId
  const reactions = msg.reactions || []

  // Resolve a Slack user ID to a display name using the members list
  function resolveName(userId) {
    const m = members.find(m => m.id === userId)
    return m ? (m.displayName || m.realName || m.name || userId) : userId
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setHoveredReaction(null) }}
      style={{
        display: 'flex', gap: 10, padding: '8px 0',
        borderBottom: '0.5px solid var(--aj-border)',
        position: 'relative',
      }}
    >
      {/* Avatar */}
      <div style={{
        width: 28, height: 28, borderRadius: '50%', background: 'var(--aj-bg-muted)', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: '"DM Sans",sans-serif', fontSize: 9, fontWeight: 600, color: 'var(--aj-text)',
        marginTop: 1,
      }}>
        {initials(authorName)}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header row — stable width, no conditional elements */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 600, color: 'var(--aj-text)' }}>
            {authorName}
          </span>
          {isFirst && (
            <span style={{
              fontFamily: '"DM Sans",sans-serif', fontSize: 8, color: '#1D9E75',
              letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 500,
            }}>
              started
            </span>
          )}
          <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', marginLeft: 'auto' }}>
            {formatTs(msg.ts)}
          </span>
        </div>

        {/* Message text */}
        <div style={{
          fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)',
          lineHeight: 1.55, wordBreak: 'break-word',
        }}>
          <SlackText text={msg.text} members={members} />
        </div>

        {/* Image attachments — files.completeUploadExternal posts (or anything else
            shared into the thread straight from Slack). Proxied through
            api/slack-file.js since Slack's own file URLs need the bot token. */}
        {(msg.files || []).filter(f => f.mimetype?.startsWith('image/')).map(f => (
          <a key={f.id} href={imageProxyUrl(f.url_private)} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: 6 }}>
            <img
              src={imageProxyUrl(f.url_private)}
              alt={f.title || f.name || 'image'}
              style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 6, border: '0.5px solid var(--aj-border)', display: 'block' }}
            />
          </a>
        ))}

        {/* Reaction chips — only rendered when at least one reaction exists.
            These are always in-flow so they don't pop in/out on hover.
            All reactions from Slack are displayed; quick-react (toggle) is
            only enabled for the 4 emojis in QUICK_REACTIONS. */}
        {reactions.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
            {reactions.map(r => {
              const qr = QUICK_REACTIONS.find(q => q.name === r.name)
              const isMine = !!(currentSlackUserId && (r.users || []).includes(currentSlackUserId))
              const reactorNames = (r.users || []).map(resolveName)
              const tooltipText = reactorNames.length > 0
                ? reactorNames.join(', ')
                : r.count > 0 ? `${r.count} reaction${r.count > 1 ? 's' : ''}` : ''
              // Use the broad EMOJI_MAP for display; fall back to :name: for unknowns
              const display = EMOJI_MAP[r.name] || `:${r.name}:`
              const canToggle = !!qr   // only the 4 quick-react emojis are interactive

              return (
                <div key={r.name} style={{ position: 'relative', flexShrink: 0 }}>
                  {/* Tooltip */}
                  {hoveredReaction === r.name && tooltipText && (
                    <div style={{
                      position: 'absolute', bottom: 'calc(100% + 5px)', left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#07272D', color: '#fff',
                      borderRadius: 4, padding: '4px 8px',
                      fontFamily: '"DM Sans",sans-serif', fontSize: 10, lineHeight: 1.4,
                      whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 200,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.30)',
                    }}>
                      {tooltipText}
                    </div>
                  )}
                  <button
                    onClick={e => { e.stopPropagation(); if (canToggle) onReact(msg.ts, r.name, !isMine) }}
                    onMouseEnter={() => setHoveredReaction(r.name)}
                    onMouseLeave={() => setHoveredReaction(null)}
                    title={canToggle ? (isMine ? `Remove ${r.name}` : `Add ${r.name}`) : `${r.count} reaction${r.count !== 1 ? 's' : ''}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      background: isMine ? 'rgba(29,158,117,0.10)' : 'var(--aj-bg-subtle)',
                      border: `0.5px solid ${isMine ? 'rgba(29,158,117,0.35)' : 'var(--aj-border)'}`,
                      borderRadius: 12, padding: '2px 8px 2px 5px',
                      cursor: canToggle ? 'pointer' : 'default', lineHeight: 1,
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{display}</span>
                    <span style={{
                      fontFamily: '"DM Mono",monospace', fontSize: 10, fontWeight: 600,
                      color: isMine ? '#1D9E75' : 'var(--aj-text-muted)',
                    }}>
                      {r.count}
                    </span>
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Floating action bar — position:absolute so it NEVER affects message height ── */}
      {hovered && (
        <div style={{
          position: 'absolute', top: 6, right: 0,
          display: 'flex', alignItems: 'center', gap: 2,
          background: 'var(--aj-bg-elevated)',
          border: '0.5px solid var(--aj-border-mid)',
          borderRadius: 8, padding: '2px 4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.20)',
          zIndex: 10,
        }}>
          {/* Quick-react buttons — only show for reactions not already added */}
          {QUICK_REACTIONS.map(qr => {
            const already = reactions.find(r => r.name === qr.name)
            const isMine = already && currentSlackUserId && (already.users || []).includes(currentSlackUserId)
            return (
              <button
                key={qr.name}
                onClick={e => { e.stopPropagation(); onReact(msg.ts, qr.name, !isMine) }}
                title={isMine ? `Remove ${qr.name}` : `React with ${qr.name}`}
                style={{
                  background: isMine ? 'rgba(29,158,117,0.12)' : 'none',
                  border: 'none', cursor: 'pointer',
                  padding: '2px 3px', lineHeight: 1, borderRadius: 4,
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (!isMine) e.currentTarget.style.background = 'var(--aj-bg-subtle)' }}
                onMouseLeave={e => { if (!isMine) e.currentTarget.style.background = 'none' }}
              >
                <span style={{ fontSize: 14 }}>{qr.emoji}</span>
              </button>
            )
          })}
          {/* Delete — only for own messages */}
          {isOwn && (
            <>
              <div style={{ width: 0.5, height: 14, background: 'var(--aj-border-mid)', margin: '0 2px', flexShrink: 0 }} />
              <button
                onClick={() => onDelete(msg.ts)}
                title="Delete message"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '2px 4px', color: 'var(--aj-text-mid)', lineHeight: 1,
                  fontSize: 14, borderRadius: 4, transition: 'background 0.1s, color 0.1s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#c0392b'; e.currentTarget.style.background = 'rgba(192,57,43,0.07)' }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--aj-text-mid)'; e.currentTarget.style.background = 'none' }}
              >
                ×
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Main component ──────────────────────────────────────────────────────── */

export default function SlackThreadPanel({ entityId, entityType = 'milestone', entityName, projectId, projectName, threadTs, onClose, onThreadStarted, onMessageSent, mentionName = '', variant = 'overlay', zIndex = null }) {
  const [isOpen,        setIsOpen]       = useState(false)
  const [messages,      setMessages]     = useState(null)   // null=loading, []+=loaded
  const [error,         setError]        = useState('')
  const [starting,      setStarting]     = useState(false)
  const [reply,         setReply]        = useState('')
  const [sending,       setSending]      = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [permalink,     setPermalink]    = useState('')
  const [members,       setMembers]      = useState([])
  const [mentionItems,  setMentionItems] = useState([])
  const [mentionIdx,    setMentionIdx]   = useState(0)
  // null = checking, false = not connected, true = connected
  const [slackConnected, setSlackConnected] = useState(null)
  const [slackUserId,    setSlackUserId]    = useState(null)   // caller's Slack user ID (for delete button)
  // Active thread ts — set from prop or after startDiscussion; drives polling
  const [activeThreadTs, setActiveThreadTs] = useState(threadTs || null)
  const [forceNoThread, setForceNoThread] = useState(false)

  const messagesEndRef = useRef(null)
  const messagesScrollRef = useRef(null)   // the overflow:auto messages container
  const textareaRef    = useRef(null)
  const clerkTokenRef  = useRef(null)   // used only for the initial checkConnection call

  // Auto-scroll gating: only snap to bottom on a poll/refresh when the user is already at the bottom.
  // Refs (not state) so the polling closure always reads the live value.
  const atBottomRef    = useRef(true)    // is the view near the bottom right now
  const didInitScroll  = useRef(false)   // have we done the one-time initial scroll-to-bottom
  const lastSeenTsRef  = useRef(null)    // newest message ts the user has seen at the bottom
  const [hasNewBelow, setHasNewBelow] = useState(false)  // new messages arrived while scrolled up

  function scrollToBottom(smooth) {
    messagesEndRef.current?.scrollIntoView(smooth === false ? undefined : { behavior: 'smooth' })
  }
  // Track whether the user is at the bottom; clear the "new below" pill once they catch up.
  function onMessagesScroll() {
    const el = messagesScrollRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80   // px tolerance
    atBottomRef.current = atBottom
    if (atBottom && hasNewBelow) setHasNewBelow(false)
  }
  function jumpToLatest() {
    atBottomRef.current = true
    setHasNewBelow(false)
    scrollToBottom(true)
  }

  // Always get a fresh Clerk JWT — tokens expire in ~60s so we must never cache them
  // across user actions. Clerk refreshes the session silently; this just gets the current one.
  async function getFreshToken() {
    try {
      return typeof window.__clerkGetToken === 'function'
        ? await window.__clerkGetToken()
        : null
    } catch {
      return null
    }
  }

  /* Mount animation */
  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setIsOpen(true)))
  }, [])

  /* Auto-grow the reply textarea upward as lines wrap (capped at maxHeight via
     overflowY:auto). Reset to 'auto' first so it can shrink when text is removed. */
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }, [reply])

  /* Load workspace members for @mention autocomplete */
  useEffect(() => {
    getMembers().then(res => {
      if (!res.members) return
      setMembers(res.members)
      // Start the reply with an @mention of the named person (matched on full name, else first
      // initial + last name, so "Matthew Gummow" finds a Slack "Matt Gummow").
      const want = String(mentionName || '').trim().toLowerCase()
      if (!want) return
      const parts = want.split(/\s+/)
      const m = res.members.find(x => [x.realName, x.displayName].some(n => String(n || '').trim().toLowerCase() === want))
        || res.members.find(x => { const q = String(x.realName || '').trim().toLowerCase().split(/\s+/); return q.length > 1 && q[q.length - 1] === parts[parts.length - 1] && q[0][0] === parts[0][0] })
      if (m && m.name) setReply(r => r || '@' + m.name + ' ')
    }).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* Check whether the user has connected their personal Slack account */
  useEffect(() => {
    async function check() {
      try {
        const token = typeof window.__clerkGetToken === 'function'
          ? await window.__clerkGetToken()
          : null
        clerkTokenRef.current = token
        if (!token) { setSlackConnected(false); return }
        const res = await checkSlackConnection(token)
        setSlackConnected(!!res.connected)
        if (res.slackUserId) setSlackUserId(res.slackUserId)
      } catch (_) {
        setSlackConnected(false)
      }
    }
    check()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* Escape key closes the panel (but not when mention dropdown is open) */
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && mentionItems.length === 0) close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mentionItems.length]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Load thread on mount */
  useEffect(() => {
    if (!threadTs) {
      setMessages([])
      return
    }
    setMessages(null)
    loadThread(threadTs)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* Poll for new messages every 5s while panel is open and a thread exists */
  useEffect(() => {
    if (!activeThreadTs) return
    const interval = setInterval(() => loadThread(activeThreadTs), 5000)
    return () => clearInterval(interval)
  }, [activeThreadTs]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Actions ──────────────────────────────────────────────────────── */

  function close() {
    setIsOpen(false)
    setTimeout(onClose, 220)
  }

  async function loadThread(ts) {
    try {
      const res = await getThread({ projectId, threadTs: ts, entityType })
      if (res.error) {
        // thread_not_found means the stored timestamp is stale — failsafe to new discussion
        if (res.error === 'thread_not_found' || res.error.startsWith('thread_not_found')) {
          setForceNoThread(true)
          setActiveThreadTs(null)
          setMessages([])
          setError('')
        } else {
          setError(res.error)
          setMessages([])
        }
      } else {
        const freshMsgs = res.messages || []
        // Merge: preserve locally-optimistic reactions when Slack doesn't return them yet
        // (reactions:read scope may be absent from the bot token)
        setMessages(prev => {
          if (!prev || prev.length === 0) return freshMsgs
          const prevMap = {}
          prev.forEach(m => { prevMap[m.ts] = m })
          return freshMsgs.map(m => {
            if (m.reactions && m.reactions.length > 0) return m
            const p = prevMap[m.ts]
            if (p?.reactions && p.reactions.length > 0) return { ...m, reactions: p.reactions }
            return m
          })
        })
        if (res.permalink) setPermalink(res.permalink)
        const lastTs = freshMsgs.length ? freshMsgs[freshMsgs.length - 1].ts : null
        if (freshMsgs.length > 0) {
          localStorage.setItem('slack:read:' + entityId, lastTs)
        }
        // Only auto-scroll to bottom on the FIRST load or when the user is already at the bottom.
        // If they've scrolled up to read and new messages arrive, surface a non-disruptive pill instead
        // of yanking the viewport down.
        if (!didInitScroll.current) {
          didInitScroll.current = true
          lastSeenTsRef.current = lastTs
          setTimeout(() => scrollToBottom(false), 50)
        } else if (atBottomRef.current) {
          lastSeenTsRef.current = lastTs
          setTimeout(() => scrollToBottom(true), 50)
        } else if (lastTs && lastTs !== lastSeenTsRef.current) {
          setHasNewBelow(true)   // scrolled up + genuinely new content below
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load thread')
      setMessages([])
    }
  }

  async function handleStartDiscussion() {
    setStarting(true)
    setError('')
    const authorName =
      ctx.currentMember?.fields?.['Name'] ||
      ctx.currentMember?.fields?.['Member Name'] ||
      'Team'
    try {
      setForceNoThread(false)
      // Deep link that zooms back to this exact node (so the posted Slack link lands on it).
      const deepLink = (typeof ctx.entityShareUrl === 'function') ? ctx.entityShareUrl(entityType, entityId) : ''
      const res = await startDiscussion(
        { entityId, entityType, entityName, projectId, projectName, startedBy: authorName, deepLink },
        await getFreshToken()
      )
      if (res.error) { setError(res.error); return }
      if (res.warning) setError(res.warning)   // non-fatal — thread usable but won't persist
      if (res.permalink) setPermalink(res.permalink)
      onThreadStarted?.(res.ts)
      window.dispatchEvent(new CustomEvent('outline-render'))
      localStorage.setItem('slack:read:' + entityId, res.ts)
      setActiveThreadTs(res.ts)
      await loadThread(res.ts)
    } catch (err) {
      setError(err.message || 'Failed to start discussion')
    } finally {
      setStarting(false)
    }
  }

  async function handleReply() {
    const msg = reply.trim()
    if (!msg || sending) return
    setSending(true)
    setError('')
    const currentThreadTs = (messages && messages[0]?.ts) || threadTs
    const authorName =
      ctx.currentMember?.fields?.['Name'] ||
      ctx.currentMember?.fields?.['Member Name'] ||
      'Team'
    try {
      const res = await replyToThread(
        { projectId, threadTs: currentThreadTs, message: msg, authorName, entityType },
        await getFreshToken()
      )
      if (res.error) { setError(res.error); return }
      setReply('')
      onMessageSent?.(msg)
      atBottomRef.current = true   // sending my own message → follow it to the bottom
      setHasNewBelow(false)
      await loadThread(currentThreadTs)
    } catch (err) {
      setError(err.message || 'Failed to send reply')
    } finally {
      setSending(false)
    }
  }

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
      reader.onerror = () => reject(new Error('Could not read the pasted image'))
      reader.readAsDataURL(file)
    })
  }

  // A pasted/copied image is content just like a typed message — it starts the
  // thread itself if none exists yet (mirrors handleStartDiscussion's bookkeeping),
  // or replies into the existing one (mirrors handleReply's). Whatever's already
  // typed in the composer rides along as the image's caption, Slack's own convention.
  async function handlePasteImage(file) {
    if (uploadingImage) return
    setUploadingImage(true)
    setError('')
    const currentThreadTs = (messages && messages[0]?.ts) || activeThreadTs || threadTs || ''
    const comment = reply.trim() || undefined
    try {
      const imageBase64 = await fileToBase64(file)
      const res = await uploadImage(
        { entityId, entityType, entityName, projectId, projectName, threadTs: currentThreadTs, imageBase64, filename: file.name, comment },
        await getFreshToken()
      )
      if (res.error) { setError(res.error); return }
      if (res.warning) setError(res.warning)
      if (comment) setReply('')
      if (!currentThreadTs && res.ts) {
        onThreadStarted?.(res.ts)
        window.dispatchEvent(new CustomEvent('outline-render'))
        localStorage.setItem('slack:read:' + entityId, res.ts)
        setActiveThreadTs(res.ts)
      }
      atBottomRef.current = true   // sending my own content → follow it to the bottom
      setHasNewBelow(false)
      await loadThread(res.ts || currentThreadTs)
    } catch (err) {
      setError(err.message || 'Failed to upload image')
    } finally {
      setUploadingImage(false)
    }
  }

  function handleTextareaPaste(e) {
    const items = e.clipboardData?.items
    if (!items) return
    const imageItem = Array.from(items).find(it => it.kind === 'file' && it.type.startsWith('image/'))
    if (!imageItem) return   // no image on the clipboard — let normal text paste proceed
    e.preventDefault()
    const file = imageItem.getAsFile()
    if (file) handlePasteImage(file)
  }

  // Shared by the initial "Connect Slack" button and the connected-state "Reconnect"
  // link — re-running this is the only way an already-connected user picks up a
  // scope added to the Slack app AFTER they first connected (api/slack-connect.js's
  // upsert-by-clerk-user-id cleanly replaces the old token either way).
  async function openSlackConnect() {
    try {
      const token = await getFreshToken()
      if (!token) return
      const res = await getSlackConnectUrl(token)
      if (res.url) window.open(res.url, '_blank', 'width=600,height=700,noopener')
    } catch (_) {}
  }

  // Local-only reaction mutation — used for the optimistic update and to revert it
  // if Slack rejects the change.
  function applyLocalReaction(messageTs, emojiName, adding) {
    setMessages(prev => {
      if (!prev) return prev
      return prev.map(m => {
        if (m.ts !== messageTs) return m
        const reactions = (m.reactions || []).map(r => ({ ...r, users: [...(r.users || [])] }))
        const idx = reactions.findIndex(r => r.name === emojiName)
        if (adding) {
          if (idx === -1) {
            reactions.push({ name: emojiName, count: 1, users: slackUserId ? [slackUserId] : [] })
          } else {
            reactions[idx].count += 1
            if (slackUserId && !reactions[idx].users.includes(slackUserId)) {
              reactions[idx].users.push(slackUserId)
            }
          }
        } else {
          if (idx !== -1) {
            reactions[idx].count = Math.max(0, reactions[idx].count - 1)
            reactions[idx].users = reactions[idx].users.filter(u => u !== slackUserId)
            if (reactions[idx].count === 0) reactions.splice(idx, 1)
          }
        }
        return { ...m, reactions }
      })
    })
  }

  function reactionErrorMessage(err) {
    if (err === 'missing_scope') return "Reaction needs Slack's reactions:write permission — reconnect your Slack account (or an admin must add the scope)."
    return 'Reaction not saved to Slack: ' + err
  }

  async function handleReact(messageTs, emojiName, adding) {
    applyLocalReaction(messageTs, emojiName, adding)   // optimistic
    try {
      const fn = adding ? addReaction : removeReaction
      const res = await fn({ projectId, messageTs, name: emojiName, entityType }, await getFreshToken())
      // The API resolves with { error } (it does not throw) — surface it instead of
      // silently keeping an optimistic chip that never reached Slack.
      if (res?.error) {
        applyLocalReaction(messageTs, emojiName, !adding)   // revert
        setError(reactionErrorMessage(res.error))
        return
      }
      const ts = activeThreadTs || threadTs
      if (ts) setTimeout(() => loadThread(ts), 600)
    } catch (e) {
      applyLocalReaction(messageTs, emojiName, !adding)     // revert
      setError(reactionErrorMessage(e.message || String(e)))
    }
  }

  async function handleDeleteMessage(messageTs) {
    // Optimistic: remove from local state immediately
    setMessages(prev => (prev || []).filter(m => m.ts !== messageTs))
    try {
      const res = await deleteMessage({ projectId, messageTs, entityType }, await getFreshToken())
      if (res.error) {
        // Restore on failure by reloading the thread
        setError(res.error)
        const ts = activeThreadTs || threadTs
        if (ts) loadThread(ts)
      }
    } catch (err) {
      setError(err.message || 'Failed to delete message')
      const ts = activeThreadTs || threadTs
      if (ts) loadThread(ts)
    }
  }

  /* ── @mention autocomplete ────────────────────────────────────────── */

  function updateMentionDropdown(val, cursor) {
    if (!members.length) return
    const mention = getMentionAtCursor(val, cursor)
    if (mention !== null) {
      const q = mention.query.toLowerCase()
      const filtered = members.filter(m =>
        (m.displayName || '').toLowerCase().startsWith(q) ||
        (m.realName    || '').toLowerCase().startsWith(q) ||
        (m.name        || '').toLowerCase().startsWith(q)
      ).slice(0, 6)
      setMentionItems(filtered)
      setMentionIdx(0)
    } else {
      setMentionItems([])
    }
  }

  function insertMention(member) {
    const el = textareaRef.current
    const cursor = el?.selectionStart ?? reply.length
    const mention = getMentionAtCursor(reply, cursor)
    if (!mention) return
    const before = reply.slice(0, mention.start)
    const after  = reply.slice(cursor)
    const insert = `@${member.name} `
    setReply(before + insert + after)
    setMentionItems([])
    const newPos = mention.start + insert.length
    setTimeout(() => { el?.setSelectionRange(newPos, newPos); el?.focus() }, 0)
  }

  /* ── Derived state ────────────────────────────────────────────────── */

  const hasThread = Array.isArray(messages) && messages.length > 0
  const isLoading = messages === null
  const noThread  = forceNoThread || (Array.isArray(messages) && messages.length === 0 && !activeThreadTs)

  /* ── Render ───────────────────────────────────────────────────────── */

  const isDock = variant === 'dock'

  const content = (
    <>
      {/* zIndex: lift the overlay + panel above a full-screen lockout (the morning check opens this
          over its scrim). The panel portals to <body>, so a wrapping element's z-index can't do it. */}
      {!isDock && <div className="aj-slack-overlay" onMouseDown={close} style={zIndex ? { zIndex } : undefined} />}

      <div className={isDock ? 'aj-slack-dock' : `aj-slack-panel${isOpen ? ' aj-slack-panel--open' : ''}`}
        style={!isDock && zIndex ? { zIndex: zIndex + 1 } : undefined}>

        {/* ── Header ─────────────────────────────────────────────────── *
         * Dock mode (Matt, 2026-08-20: "a major waste of screen real estate")
         * collapses to one compact row — the entityName subtitle is dropped
         * since AlderCAD's dock always shows the same "AlderCAD Model" label,
         * pure repetition there. Overlay mode (milestones/sheets/BIM systems,
         * a different entity per panel) keeps the two-line title — the
         * subtitle is the only thing telling you WHICH entity you're looking
         * at there, so it stays load-bearing. */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: isDock ? '5px 8px' : '14px 16px',
          borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0, gap: isDock ? 6 : 8,
        }}>
          <svg width={isDock ? 12 : 14} height={isDock ? 12 : 14} viewBox="0 0 24 24" fill="none"
               stroke="var(--aj-text-mid)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
               style={{ flexShrink: 0 }}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          {isDock ? (
            <div style={{
              flex: 1, minWidth: 0, fontFamily: '"DM Sans",sans-serif', fontWeight: 600, fontSize: 11,
              color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              Discussion
            </div>
          ) : (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: '"DM Sans",sans-serif', fontWeight: 600, fontSize: 10,
                color: 'var(--aj-text)', letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>
                Discussion
              </div>
              <div style={{
                fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {entityName}
              </div>
            </div>
          )}

          {permalink && (
            <a
              href={permalink}
              target="_blank"
              rel="noopener noreferrer"
              title="Open thread in Slack"
              style={{
                fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)',
                textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center',
                gap: 2, padding: isDock ? '2px 5px' : '3px 6px', borderRadius: 3,
                border: '0.5px solid var(--aj-border-mid)',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--aj-text)'; e.currentTarget.style.borderColor = 'var(--aj-border-mid)' }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--aj-text-muted)'; e.currentTarget.style.borderColor = 'var(--aj-border-mid)' }}
            >
              Slack ↗
            </a>
          )}

          <button
            onClick={close}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: isDock ? 2 : 4, flexShrink: 0 }}
            title="Close"
          >
            <svg width={isDock ? 12 : 14} height={isDock ? 12 : 14} viewBox="0 0 16 16" fill="none">
              <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div ref={messagesScrollRef} onScroll={onMessagesScroll} style={{ flex: 1, overflowY: 'auto', padding: '0 16px', position: 'relative' }}>
          {isLoading && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', padding: '32px 0', textAlign: 'center' }}>
              Loading…
            </div>
          )}

          {noThread && (
            <div
              tabIndex={0}
              onPaste={handleTextareaPaste}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200, gap: 14, padding: '40px 0', outline: 'none' }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
                   stroke="var(--aj-border-mid)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', textAlign: 'center' }}>
                {uploadingImage ? 'Uploading image…' : 'No discussion yet'}
              </div>
              <button
                onClick={handleStartDiscussion}
                disabled={starting || uploadingImage}
                style={{
                  background: '#07272D', color: 'white', border: 'none', borderRadius: 4,
                  padding: '8px 18px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
                  fontWeight: 500, cursor: (starting || uploadingImage) ? 'not-allowed' : 'pointer',
                  opacity: (starting || uploadingImage) ? 0.55 : 1, transition: 'opacity 0.12s',
                }}
              >
                {starting ? 'Starting…' : 'Start Discussion'}
              </button>
              {!uploadingImage && (
                <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', textAlign: 'center' }}>
                  or click here and paste an image
                </div>
              )}
            </div>
          )}

          {!isLoading && !noThread && hasThread && (
            <div style={{ paddingTop: 8, paddingBottom: 8 }}>
              {messages.map((msg, i) => (
                <MessageRow
                  key={msg.ts} msg={msg} isFirst={i === 0} members={members}
                  currentSlackUserId={slackUserId}
                  onDelete={handleDeleteMessage}
                  onReact={handleReact}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}

          {!isLoading && !noThread && !hasThread && threadTs && !error && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', padding: '32px 0', textAlign: 'center' }}>
              Thread not found —{' '}
              <button onClick={() => loadThread(threadTs)}
                style={{ background: 'none', border: 'none', color: 'var(--aj-text-muted)', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, fontFamily: 'inherit', padding: 0 }}>
                Retry
              </button>
            </div>
          )}

          {error && (
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b', padding: '10px 0', textAlign: 'center' }}>
              {error}{' '}
              <button
                onClick={() => setError('')}
                style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', textDecoration: 'underline', fontSize: 11, fontFamily: 'inherit', padding: 0 }}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>

        {/* "New messages" pill — shown when content arrived while the user was scrolled up. Click to
            jump to the latest; non-disruptive (never moves the viewport on its own). */}
        {hasNewBelow && (
          <button
            onClick={jumpToLatest}
            style={{
              position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: 76, zIndex: 20,
              background: '#07272D', color: 'white', border: 'none', borderRadius: 20,
              padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 500,
              cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.30)',
            }}
          >
            New messages ↓
          </button>
        )}

        {/* ── Reply input ────────────────────────────────────────────── */}
        {(hasThread || threadTs) && (
          <div style={{ padding: '10px 16px 14px', borderTop: '0.5px solid var(--aj-border)', flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>

              {/* @mention dropdown */}
              {mentionItems.length > 0 && (
                <div style={{
                  position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, right: 0,
                  background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)',
                  borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.20)',
                  zIndex: 10, overflow: 'hidden',
                }}>
                  {mentionItems.map((m, i) => (
                    <div
                      key={m.id}
                      onMouseDown={e => { e.preventDefault(); insertMention(m) }}
                      onMouseEnter={() => setMentionIdx(i)}
                      style={{
                        padding: '6px 10px', cursor: 'pointer',
                        background: i === mentionIdx ? 'var(--aj-bg-subtle)' : 'transparent',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}
                    >
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', background: 'var(--aj-bg-muted)', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: '"DM Sans",sans-serif', fontSize: 8, fontWeight: 600, color: 'var(--aj-text)',
                      }}>
                        {initials(m.displayName || m.realName || m.name)}
                      </div>
                      <div>
                        <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text)' }}>
                          {m.displayName || m.realName || m.name}
                        </span>
                        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)', marginLeft: 6 }}>
                          @{m.name}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-end',
                border: '0.5px solid var(--aj-border-mid)', borderRadius: 6,
                padding: '6px 10px', background: 'var(--aj-bg-elevated)',
              }}>
                <textarea
                  ref={textareaRef}
                  value={reply}
                  onChange={e => {
                    const val = e.target.value
                    setReply(val)
                    updateMentionDropdown(val, e.target.selectionStart)
                  }}
                  onKeyDown={e => {
                    if (mentionItems.length > 0) {
                      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => Math.min(i + 1, mentionItems.length - 1)); return }
                      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIdx(i => Math.max(i - 1, 0)); return }
                      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) { e.preventDefault(); insertMention(mentionItems[mentionIdx]); return }
                      if (e.key === 'Escape')    { e.stopPropagation(); setMentionItems([]); return }
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleReply()
                    }
                  }}
                  onPaste={handleTextareaPaste}
                  placeholder={uploadingImage ? 'Uploading image…' : 'Reply… (paste an image to share it)'}
                  rows={1}
                  style={{
                    flex: 1, border: 'none', outline: 'none', resize: 'none', background: 'transparent',
                    fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)',
                    lineHeight: 1.5, minHeight: 20, maxHeight: 120, overflowY: 'auto',
                  }}
                />
                <button
                  onClick={handleReply}
                  disabled={!reply.trim() || sending}
                  style={{
                    background: reply.trim() ? '#07272D' : 'var(--aj-bg-muted)',
                    color: reply.trim() ? 'white' : 'var(--aj-text-muted)',
                    border: 'none', borderRadius: 4, padding: '4px 10px',
                    fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500,
                    cursor: reply.trim() && !sending ? 'pointer' : 'not-allowed',
                    transition: 'background 0.12s, color 0.12s', flexShrink: 0,
                  }}
                >
                  {sending ? '…' : 'Send'}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 4, gap: 8 }}>
              {slackConnected === false && (
                <button
                  onClick={openSlackConnect}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)',
                    textDecoration: 'underline', textUnderlineOffset: 2, flexShrink: 0,
                  }}
                  title="Connect your Slack account to post as yourself (no APP badge)"
                >
                  Connect Slack to post as yourself
                </button>
              )}
              {slackConnected === true && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: '#1D9E75' }}>
                    ✓ Posting as you
                  </span>
                  {/* re-run the connect flow — the only way to pick up a NEWLY added
                      scope (e.g. files:write, added 2026-08-20 for image uploads);
                      the stored token from an earlier connection never gains scopes
                      on its own, so an upload silently posts as the bot until this
                      is clicked (api/slack.js uploadImage's bot-token fallback). */}
                  <button
                    onClick={openSlackConnect}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)',
                      textDecoration: 'underline', textUnderlineOffset: 2,
                    }}
                    title="Re-authorize Slack — needed after new permissions (like image uploads) are added"
                  >
                    Reconnect
                  </button>
                </span>
              )}
              {uploadingImage && (
                <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', flexShrink: 0 }}>
                  Uploading image…
                </span>
              )}
              <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', marginLeft: 'auto', flexShrink: 0 }}>
                Shift+Enter for new line
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  )

  return isDock ? content : createPortal(content, document.body)
}
